import { Box, Link, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useState } from 'react';
import ExtensionIcon from '@mui/icons-material/Extension';
import RouterLink from 'components/common/Link';
import Modal from 'components/common/Modal';
import { useExtensionDetection, FIREFOX_EXTENSION_SETTINGS_HREF } from 'lib/useExtensionDetection';

const extensionWhyUsefulBullets = [
  'Add candidates to jobs or contacts to clients right from LinkedIn or GitHub.',
  "Unlike other extensions where you're still stuck doing manual candidate updates and outreach, Harbor lets you do it all at once.",
  "Auto-source a candidate's email address within the extension with one click, update a candidate's status, and kick off an email sequence without ever leaving the page you're on.",
];

export default function ExtensionChecker() {
  const { installed, browser, storeUrl, installLabel } = useExtensionDetection();
  const [modalOpen, setModalOpen] = useState(false);
  const isFirefox = browser === 'firefox';
  const canInstallSafari = browser !== 'safari' || Boolean(storeUrl);

  if (installed) return null;

  return (
    <>
      <Box
        onClick={() => setModalOpen(true)}
        sx={{
          backgroundColor: 'rgba(251, 191, 36, 0.15)',
          margin: '0 8px 16px 8px',
          color: 'rgb(253, 224, 71)',
          fontSize: '12px',
          padding: '6px 12px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          border: '1px solid rgba(251, 191, 36, 0.4)',
          boxShadow: '0 0 0 1px rgba(251, 191, 36, 0.1)',
          cursor: 'pointer',
        }}
      >
        <ExtensionIcon sx={{ fontSize: '16px' }} />
        <span style={{ fontWeight: 500 }}>Browser Extension is not installed</span>
      </Box>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Why use the Browser Extension?`}
        renderContent={() => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              pt: 0.5,
            }}
          >
            <List
              component="ul"
              dense
              disablePadding
              sx={{ color: 'text.secondary', mt: -0.5, pl: 2.5, listStyleType: 'disc' }}
            >
              {extensionWhyUsefulBullets.map(item => (
                <ListItem component="li" key={item} sx={{ display: 'list-item', py: 0.25, pl: 0 }}>
                  <ListItemText
                    primary={item}
                    sx={{
                      m: 0,
                      '& .MuiListItemText-primary': {
                        fontSize: '0.9375rem',
                        lineHeight: 1.6,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                pt: 2,
                mt: 0.5,
              }}
            >
              {isFirefox && !storeUrl ? (
                <Box>
                  <RouterLink
                    href={FIREFOX_EXTENSION_SETTINGS_HREF}
                    onClick={() => setModalOpen(false)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: '0.9375rem',
                      fontWeight: 500,
                    }}
                  >
                    <ExtensionIcon sx={{ fontSize: '18px' }} />
                    Install the Firefox extension
                  </RouterLink>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Download the extension from Settings and load it temporarily in Firefox.
                  </Typography>
                </Box>
              ) : canInstallSafari ? (
                <Link
                  href={storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                  }}
                >
                  <ExtensionIcon sx={{ fontSize: '18px' }} />
                  {installLabel}
                </Link>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Harbor for Safari is coming to the Mac App Store. Until then, you can install the extension
                    manually.
                  </Typography>
                  <RouterLink
                    href="/settings/candidates?tab=4"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: '0.9375rem',
                      fontWeight: 500,
                    }}
                  >
                    <ExtensionIcon sx={{ fontSize: '18px' }} />
                    See manual install steps
                  </RouterLink>
                </Box>
              )}
            </Box>
          </Box>
        )}
      />
    </>
  );
}
